/**
 * UnifiedInput.tsx
 * 
 * Consolidated input component that replaces:
 * - AIFieldInput.tsx
 * - MinimalFieldInput.tsx
 * - Basic Input.tsx functionality
 * 
 * Supports all field types with optional AI suggestions
 */

import React, { forwardRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { EyeIcon, EyeSlashIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { Sparkles, Check, X, Edit3, AlertTriangle } from 'lucide-react';

export interface AISuggestion {
  value: any;
  confidence: number;
  sourceLocation?: string;
}

export interface UnifiedInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>, 'size'> {
  label?: string;
  error?: string;
  helpText?: string;
  description?: string;
  icon?: React.ReactNode | React.ComponentType<React.SVGProps<SVGSVGElement>>;
  showPasswordToggle?: boolean;
  variant?: 'default' | 'filled' | 'outline' | 'minimal';
  size?: 'sm' | 'md' | 'lg';
  fieldType?: 'text' | 'textarea' | 'number' | 'select' | 'password';
  options?: string[];
  
  // AI Integration
  aiSuggestion?: AISuggestion;
  onAcceptSuggestion?: () => void;
  onRejectSuggestion?: () => void;
  showAIIndicator?: boolean;
  
  // Advanced Features
  expandable?: boolean;
  characterCount?: boolean;
}

const UnifiedInput = forwardRef<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement, UnifiedInputProps>(({
  label,
  error,
  helpText,
  description,
  icon,
  showPasswordToggle = false,
  variant = 'default',
  size = 'md',
  type = 'text',
  fieldType = 'text',
  options = [],
  className = '',
  placeholder,
  disabled = false,
  required = false,
  
  // AI Integration
  aiSuggestion,
  onAcceptSuggestion,
  onRejectSuggestion,
  showAIIndicator = true,
  
  // Advanced Features
  expandable = false,
  characterCount = false,
  
  ...props
}, ref) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [hasValue, setHasValue] = useState(Boolean(props.value || props.defaultValue));
  const [isExpanded, setIsExpanded] = useState(false);
  const [localValue, setLocalValue] = useState(props.value || '');

  // Determine actual field type
  const actualFieldType = fieldType || (type === 'password' ? 'password' : 'text');
  const actualType = showPasswordToggle && actualFieldType === 'password' && showPassword ? 'text' : actualFieldType;
  const isFloatingLabel = Boolean(label) && (variant === 'default' || variant === 'filled');

  // Base classes
  const baseClasses = 'block w-full transition-all duration-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed';
  
  // Variant classes
  const variantClasses = {
    default: `border border-gray-300 bg-white focus:ring-2 focus:ring-accent-500 focus:ring-opacity-20 focus:border-accent-500 ${error ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`,
    filled: `border-0 bg-white focus:bg-white focus:ring-2 focus:ring-accent-500 focus:ring-opacity-20 ${error ? 'focus:ring-red-500' : ''}`,
    outline: `border-2 border-gray-300 bg-transparent focus:border-accent-500 ${error ? 'border-red-300 focus:border-red-500' : ''}`,
    minimal: `border border-gray-200 bg-white hover:border-gray-300 focus:border-accent-500 focus:ring-1 focus:ring-accent-500 focus:shadow-sm ${error ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`
  };

  // Size classes - ensuring minimum 48px touch targets for mobile
  const sizeClasses = {
    sm: 'px-3 py-2 text-sm rounded min-h-[48px]',
    md: 'px-4 py-3 text-base rounded-md min-h-[48px]',
    lg: 'px-6 py-4 text-lg rounded-md min-h-[48px]'
  };

  // AI suggestion styling
  const getAIStyling = () => {
    if (error) return '';
    if (!aiSuggestion) return '';
    
    const confidence = aiSuggestion.confidence;
    if (confidence >= 0.8) {
      return 'border-green-300 bg-green-50 focus:border-green-500 focus:ring-green-500';
    } else if (confidence >= 0.5) {
      return 'border-yellow-300 bg-yellow-50 focus:border-yellow-500 focus:ring-yellow-500';
    } else {
      return 'border-orange-300 bg-orange-50 focus:border-orange-500 focus:ring-orange-500';
    }
  };

  // Event handlers
  const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setIsFocused(true);
    props.onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setIsFocused(false);
    props.onBlur?.(e);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const newValue = e.target.value;
    setHasValue(Boolean(newValue));
    setLocalValue(newValue);
    props.onChange?.(e);
  };

  const handleAcceptSuggestion = () => {
    if (aiSuggestion) {
      setLocalValue(aiSuggestion.value);
      onAcceptSuggestion?.();
    }
  };

  const handleRejectSuggestion = () => {
    setLocalValue('');
    onRejectSuggestion?.();
  };

  // Render AI suggestion display
  const renderAISuggestion = () => {
    if (!aiSuggestion || variant === 'minimal') return null;

    const confidence = aiSuggestion.confidence;
    const isHighConfidence = confidence >= 0.8;
    const isMediumConfidence = confidence >= 0.5;

    return (
      <div className={`
        p-4 rounded-lg border-2 border-dashed transition-all duration-200 mb-4
        ${isHighConfidence 
          ? 'border-green-300 bg-green-50' 
          : isMediumConfidence 
            ? 'border-yellow-300 bg-yellow-50' 
            : 'border-orange-300 bg-orange-50'
        }
      `}>
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <div className={`
              w-8 h-8 rounded-full flex items-center justify-center
              ${isHighConfidence 
                ? 'bg-green-100' 
                : isMediumConfidence 
                  ? 'bg-yellow-100' 
                  : 'bg-orange-100'
              }
            `}>
              {isHighConfidence ? (
                <Sparkles className="w-5 h-5 text-green-600" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-orange-600" />
              )}
            </div>
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-gray-900">
                AI Suggestion
              </h4>
              <div className="text-xs text-gray-600">
                Confidence: {Math.round(confidence * 100)}%
              </div>
            </div>
            
            <div className="text-sm text-gray-700 mb-3 whitespace-pre-wrap">
              {aiSuggestion.value}
            </div>
            
            {aiSuggestion.sourceLocation && (
              <p className="text-xs text-gray-500 mb-3">
                Source: {aiSuggestion.sourceLocation}
              </p>
            )}
            
            <div className="flex items-center space-x-2">
              <button
                onClick={handleAcceptSuggestion}
                className="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium bg-green-100 text-green-700 hover:bg-green-200 transition-colors duration-200"
              >
                <Check className="w-5 h-5 mr-1" />
                Accept
              </button>
              <button
                onClick={handleRejectSuggestion}
                className="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium bg-red-100 text-red-700 hover:bg-red-200 transition-colors duration-200"
              >
                <X className="w-5 h-5 mr-1" />
                Reject
              </button>
              <button
                onClick={() => setIsExpanded(true)}
                className="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors duration-200"
              >
                <Edit3 className="w-5 h-5 mr-1" />
                Edit
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render icon
  const renderIcon = () => {
    if (!icon) return null;
    
    if (React.isValidElement(icon)) {
      return icon;
    }
    
    if (typeof icon === 'function') {
      const IconComponent = icon as React.ComponentType<React.SVGProps<SVGSVGElement>>;
      return <IconComponent className="h-5 w-5 text-gray-400" />;
    }
    
    return null;
  };

  // Render input based on field type
  const renderInput = () => {
    const inputClasses = `
      ${baseClasses}
      ${variantClasses[variant]}
      ${sizeClasses[size]}
      ${icon ? 'pl-10' : ''}
      ${showPasswordToggle || error ? 'pr-10' : ''}
      ${isFloatingLabel ? 'placeholder-transparent' : ''}
      text-neutral-800 placeholder-neutral-400
      ${getAIStyling()}
      ${className}
    `;

    switch (actualFieldType) {
      case 'textarea':
        return (
          <textarea
            ref={ref as React.RefObject<HTMLTextAreaElement>}
            value={localValue}
            onChange={handleChange}
            placeholder={isFloatingLabel ? '' : placeholder}
            required={required}
            disabled={disabled}
            rows={isExpanded ? 6 : 4}
            className={`${inputClasses} resize-none`}
            onFocus={handleFocus}
            onBlur={handleBlur}
            {...props}
          />
        );

      case 'select':
        return (
          <select
            ref={ref as React.RefObject<HTMLSelectElement>}
            value={localValue}
            onChange={handleChange}
            required={required}
            disabled={disabled}
            className={inputClasses}
            onFocus={handleFocus}
            onBlur={handleBlur}
            {...props}
          >
            <option value="">Select an option...</option>
            {options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case 'number':
        return (
          <input
            ref={ref as React.RefObject<HTMLInputElement>}
            type="number"
            value={localValue}
            onChange={handleChange}
            placeholder={isFloatingLabel ? '' : placeholder}
            required={required}
            disabled={disabled}
            className={inputClasses}
            onFocus={handleFocus}
            onBlur={handleBlur}
            {...props}
          />
        );

      case 'password':
        return (
          <input
            ref={ref as React.RefObject<HTMLInputElement>}
            type={actualType}
            value={localValue}
            onChange={handleChange}
            placeholder={isFloatingLabel ? '' : placeholder}
            required={required}
            disabled={disabled}
            className={inputClasses}
            onFocus={handleFocus}
            onBlur={handleBlur}
            {...props}
          />
        );

      default: // text
        return (
          <input
            ref={ref as React.RefObject<HTMLInputElement>}
            type="text"
            value={localValue}
            onChange={handleChange}
            placeholder={isFloatingLabel ? '' : placeholder}
            required={required}
            disabled={disabled}
            className={inputClasses}
            onFocus={handleFocus}
            onBlur={handleBlur}
            {...props}
          />
        );
    }
  };

  return (
    <div className="w-full">
      {/* AI Suggestion Display */}
      {renderAISuggestion()}
      
      <div className="relative">
        {/* Static Label for non-floating variants */}
        {label && !isFloatingLabel && (
          <label className="block text-sm font-medium text-gray-900 mb-2">
            {label}
            {required && <span className="text-red-600 ml-1">*</span>}
            {aiSuggestion && showAIIndicator && (
              <span className="ml-2 text-xs text-primary-500 opacity-60 group-focus-within:opacity-100 transition-opacity">
                <Sparkles className="w-5 h-5 inline mr-1" />
                AI
              </span>
            )}
          </label>
        )}

        {/* Description */}
        {description && (
          <p className="text-sm text-gray-600 mb-3">{description}</p>
        )}

        {/* Input Container */}
        <div className="relative">
          {/* Leading Icon */}
          {icon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              {renderIcon()}
            </div>
          )}

          {/* Input Field */}
          {renderInput()}

          {/* Trailing Icons */}
          <div className="absolute inset-y-0 right-0 flex items-center">
            {/* Error Icon */}
            {error && (
              <ExclamationCircleIcon className="h-5 w-5 text-red-600 mr-3" />
            )}

            {/* Password Toggle */}
            {showPasswordToggle && actualFieldType === 'password' && (
              <button
                type="button"
                className="mr-3 p-1 text-gray-400 hover:text-gray-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#DF335B] focus:ring-opacity-20 rounded-lg"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
            )}
          </div>
        </div>

        {/* Floating Label */}
        {isFloatingLabel && (
          <AnimatePresence>
            <motion.label
              className={`absolute left-4 transition-all duration-200 pointer-events-none ${
                isFocused || hasValue
                  ? '-top-2 text-xs bg-white px-1 text-primary-500'
                  : 'top-1/2 -translate-y-1/2 text-gray-500'
              } ${error && (isFocused || hasValue) ? 'text-red-600' : ''}`}
              initial={{ y: '-50%', scale: 1 }}
              animate={{
                y: isFocused || hasValue ? '-50%' : '-50%',
                scale: isFocused || hasValue ? 0.85 : 1,
                x: isFocused || hasValue ? 0 : 0
              }}
              transition={{ duration: 0.2 }}
            >
              {label}
              {required && <span className="text-red-600 ml-1">*</span>}
            </motion.label>
          </AnimatePresence>
        )}
      </div>

      {/* AI confidence indicator for minimal variant */}
      {aiSuggestion && variant === 'minimal' && (
        <div className="mt-1 opacity-0 group-focus-within:opacity-100 group-hover:opacity-100 transition-opacity">
          <div className="text-xs text-gray-500">
            AI confidence: {Math.round(aiSuggestion.confidence * 100)}%
            {aiSuggestion.sourceLocation && (
              <span className="ml-2">• From: {aiSuggestion.sourceLocation}</span>
            )}
          </div>
        </div>
      )}

      {/* Character count */}
      {characterCount && (actualFieldType === 'textarea' || actualFieldType === 'text') && localValue && (
        <div className="text-xs text-gray-400 text-right mt-1">
          {String(typeof localValue === 'object' ? JSON.stringify(localValue) : localValue).length} characters
        </div>
      )}

      {/* Error Message */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="mt-2 flex items-center text-sm text-red-600"
            id={`${props.id || 'input'}-error`}
          >
            <ExclamationCircleIcon className="h-4 w-4 mr-1 flex-shrink-0" />
            {error}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Help Text */}
      {helpText && !error && (
        <div className="mt-2 text-sm text-gray-600" id={`${props.id || 'input'}-help`}>
          {helpText}
        </div>
      )}
    </div>
  );
});

UnifiedInput.displayName = 'UnifiedInput';

export default UnifiedInput;
